/**
 * CoachFeedbackQueue (iter155) — Lists every session the coach still owes
 * post-session feedback on. Each row opens PostSessionFeedbackModal for that
 * specific (booking, student) pair. After submit we refetch and drop the
 * completed row from the queue.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ClipboardCheck, Loader2, Users, User as UserIcon, X, Send, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useSport } from "@/context/SportContext";
import PostSessionFeedbackModal from "@/components/PostSessionFeedbackModal";
import { SessionPlayerNav } from "@/components/SessionPlayerNav";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch { return String(iso).slice(0, 16); }
};

export default function CoachFeedbackQueue() {
  const { skillsBySport, sport } = useSport();
  const [items, setItems] = useState([]);
  const fbCtl = useListControls(items, { name: (it) => it.title, fields: (it) => [it.title, it.student_name], initialSort: "default" });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // {booking_id, student_id, ...}
  const [team, setTeam] = useState(null);          // {items: [rows]} team-feedback session
  // Iter173 — Custom dismiss confirmation. Replaces window.prompt so the
  // "No feedback needed" reason input stays inside the app's dark UI.
  // Iter174 — dismissTarget now carries a list so the same modal drives
  // single-row dismiss AND bulk dismiss (one shared reason applied to N sessions).
  const [dismissTarget, setDismissTarget] = useState(null); // {ids: string[], titles: string[]}
  const [dismissNote, setDismissNote] = useState("");
  const [dismissing, setDismissing] = useState(false);
  // Iter174 — bulk-dismiss mode: coach flips into it, ticks past sessions,
  // then dismisses them all with a single shared reason.
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkSelected, setBulkSelected] = useState(() => new Set());

  const toggleBulk = (bookingId) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(bookingId)) next.delete(bookingId); else next.add(bookingId);
      return next;
    });
  };

  const exitBulkMode = () => {
    setBulkMode(false);
    setBulkSelected(new Set());
  };

  const runDismiss = async () => {
    if (!dismissTarget || !dismissTarget.ids?.length) return;
    setDismissing(true);
    let ok = 0;
    let failed = 0;
    for (const sid of dismissTarget.ids) {
      try {
        await api.post("/me/coach/dismiss-feedback", {
          session_id: sid,
          note: dismissNote.trim() || null,
        });
        ok += 1;
      } catch (e) {
        failed += 1;
      }
    }
    setDismissing(false);
    if (ok && !failed) toast.success(ok === 1 ? "Session dismissed" : `Dismissed ${ok} sessions`);
    else if (ok && failed) toast.warning(`Dismissed ${ok}, failed ${failed}`);
    else toast.error("Dismiss failed");
    setDismissTarget(null);
    setDismissNote("");
    exitBulkMode();
    load();
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/coach/pending-feedback");
      setItems(data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't load pending feedback");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Deep link from a session / reminder: /coach/feedback?booking=<id>[&student=<id>] opens that form directly.
  const [deepLinked, setDeepLinked] = useState(false);
  const [navKey, setNavKey] = useState(0);
  const fromSession = new URLSearchParams(window.location.search).get("from");
  useEffect(() => {
    if (loading || deepLinked) return;
    const qs = new URLSearchParams(window.location.search);
    const ref = qs.get("booking") || qs.get("session");
    if (!ref) return;
    setDeepLinked(true);
    const sid = qs.get("student");
    const match = items.find((it) => (it.booking_id === ref || it.offering_id === ref || it.session_id === ref)
      && (!sid || it.student_id === sid));
    if (match) setSelected(match);
    else toast.message("Feedback for that session is already done — nothing left to add.");
  }, [loading, items, deepLinked]);

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="coach-feedback-queue">
      {fromSession && <SessionPlayerNav offeringId={fromSession} currentStudentId={selected?.student_id} refreshKey={navKey} />}
      <Link to="/coach/simple" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </Link>
      <div className="flex items-center gap-3 mb-2">
        <ClipboardCheck className="w-6 h-6 text-[#FF4500]" />
        <h1 className="h-display text-3xl">Sessions requiring feedback</h1>
      </div>
      <p className="text-sm text-[#A3A3A3] mb-6">
        Every past session where you still owe a player their post-session recap. Feedback creates their AI summary + updates focus areas.
      </p>

      {/* Iter174 — Bulk dismiss controls. Only visible once there are rows
          to act on; entering bulk mode swaps the dismiss buttons on each
          card for a tick checkbox. */}
      {!loading && items.length > 0 && (
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4" data-testid="bulk-dismiss-toolbar">
          <div className="text-xs text-[#525252]">
            {bulkMode ? `${bulkSelected.size} selected` : `${Object.keys(items.reduce((a, it) => (a[it.booking_id] = 1, a), {})).length} sessions`}
          </div>
          <div className="flex gap-2 flex-wrap">
            {!bulkMode ? (
              <button
                type="button"
                onClick={() => setBulkMode(true)}
                data-testid="bulk-dismiss-start"
                className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#525252] text-[#A3A3A3] hover:border-white hover:text-white inline-flex items-center gap-1"
              >
                <CheckSquare className="w-3 h-3" /> Bulk dismiss
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={exitBulkMode}
                  data-testid="bulk-dismiss-cancel"
                  className="px-3 py-1.5 text-[10px] uppercase tracking-wider border border-[#262626] text-[#A3A3A3] hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={bulkSelected.size === 0}
                  data-testid="bulk-dismiss-confirm"
                  onClick={() => {
                    const grouped = items.reduce((acc, it) => { (acc[it.booking_id] ||= []).push(it); return acc; }, {});
                    const ids = Array.from(bulkSelected);
                    const titles = ids.map((bid) => grouped[bid]?.[0]?.title || "Untitled session");
                    setDismissNote("");
                    setDismissTarget({ ids, titles });
                  }}
                  className="px-3 py-1.5 text-[10px] uppercase tracking-wider bg-[#525252] text-white hover:bg-[#404040] inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <X className="w-3 h-3" /> Dismiss {bulkSelected.size || ""}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-[#A3A3A3]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="card-surface p-8 text-center" data-testid="feedback-queue-empty">
          <div className="text-4xl mb-2">✅</div>
          <div className="h-display text-xl">You're all caught up</div>
          <div className="text-sm text-[#A3A3A3] mt-1">No sessions are waiting for feedback.</div>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-4" data-testid="feedback-queue-list">
          <ListControlsBar ctl={fbCtl} modes={["default", "az", "za"]} placeholder="Search player or session…" testid="feedback-queue" />
          {/* Iter171 — group rows by booking_id so multi-player group sessions
              get their own card with individual + team feedback options. */}
          {Object.entries(fbCtl.list.reduce((acc, it) => {
            (acc[it.booking_id] ||= []).push(it); return acc;
          }, {})).map(([bookingId, group]) => {
            const first = group[0];
            const isGroup = group.length > 1;
            return (
              <div
                key={bookingId}
                data-testid={`feedback-booking-${bookingId}`}
                className="bg-[#0A0A0A] border border-[#262626] p-4 space-y-3"
              >
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex items-start gap-2">
                    {bulkMode && (
                      <button
                        type="button"
                        onClick={() => toggleBulk(bookingId)}
                        data-testid={`bulk-select-${bookingId}`}
                        aria-label={bulkSelected.has(bookingId) ? "Untick session" : "Tick session"}
                        className="mt-0.5 text-[#A3A3A3] hover:text-white"
                      >
                        {bulkSelected.has(bookingId)
                          ? <CheckSquare className="w-4 h-4 text-[#CCFF00]" />
                          : <Square className="w-4 h-4" />}
                      </button>
                    )}
                    <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      {isGroup
                        ? <Users className="w-3.5 h-3.5 text-[#FFD700]" />
                        : <UserIcon className="w-3.5 h-3.5 text-[#66C2FF]" />}
                      <div className="text-sm font-semibold">{first.title}</div>
                      {isGroup && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-[#FFD700] text-[#FFD700]">
                          {group.length} players
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#A3A3A3]">
                      {fmtDate(first.session_start)} · {first.duration_min}min · {first.session_type?.replace(/_/g, " ")}
                    </div>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap shrink-0">
                    {isGroup && !bulkMode && (
                      <button
                        type="button"
                        onClick={() => setTeam({ items: group })}
                        data-testid={`team-feedback-${bookingId}`}
                        className="px-3 py-2 text-[11px] uppercase tracking-wider border border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700] hover:text-black inline-flex items-center gap-1"
                      >
                        <Users className="w-3 h-3" /> Team feedback
                      </button>
                    )}
                    {!bulkMode && (
                      <button
                        type="button"
                        onClick={() => {
                          setDismissNote("");
                          setDismissTarget({ ids: [bookingId], titles: [first.title] });
                        }}
                        data-testid={`dismiss-feedback-${bookingId}`}
                        className="px-3 py-2 text-[11px] uppercase tracking-wider border border-[#525252] text-[#A3A3A3] hover:border-white hover:text-white inline-flex items-center gap-1"
                      >
                        <X className="w-3 h-3" /> No feedback needed
                      </button>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  {group.map((it) => (
                    <div
                      key={it.student_id}
                      data-testid={`feedback-row-${it.booking_id}-${it.student_id}`}
                      className="flex items-center justify-between gap-3 border-t border-[#141414] pt-2"
                    >
                      <div className="text-xs text-[#E5E5E5]">
                        {it.student_name}
                        {it.is_organiser && (
                          <span
                            className="ml-1.5 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-[#CCFF00] text-[#CCFF00]"
                            data-testid={`organiser-chip-${it.booking_id}-${it.student_id}`}
                            title="This player set up the group request"
                          >
                            Organiser
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelected(it)}
                        data-testid={`open-feedback-${it.booking_id}-${it.student_id}`}
                        className="px-3 py-1.5 text-[10px] uppercase tracking-wider bg-[#CCFF00] text-black hover:opacity-90 inline-flex items-center gap-1"
                      >
                        Individual
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <PostSessionFeedbackModal
          open={!!selected}
          onClose={() => setSelected(null)}
          onSaved={() => { load(); setNavKey((k) => k + 1); }}
          afterSave={fromSession ? <SessionPlayerNav offeringId={fromSession} currentStudentId={selected.student_id} refreshKey={navKey} compact /> : null}
          bookingId={selected.booking_id}
          studentId={selected.student_id}
          studentName={selected.student_name}
          sessionTitle={selected.title}
          sport={selected.sport || sport}
        />
      )}

      {/* Iter173 — Dismiss confirmation dialog. Keeps the dark theme + adds
          a proper textarea for the optional reason (native prompt was hard
          to type long notes into on mobile). */}
      {dismissTarget && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          data-testid="dismiss-feedback-modal"
          onClick={() => !dismissing && setDismissTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#0A0A0A] border border-[#262626] p-6 relative"
          >
            <button
              type="button"
              onClick={() => !dismissing && setDismissTarget(null)}
              className="absolute top-3 right-3 text-[#A3A3A3] hover:text-white"
              aria-label="Close"
              data-testid="dismiss-feedback-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="label-mono text-[#CCFF00] mb-1">DISMISS {dismissTarget.ids.length > 1 ? `${dismissTarget.ids.length} SESSIONS` : "SESSION"}</div>
            <h2 className="h-display text-xl mb-2">No feedback needed?</h2>
            <p className="text-xs text-[#A3A3A3] mb-4 leading-relaxed">
              {dismissTarget.ids.length > 1 ? (
                <>
                  This will drop <span className="text-white">{dismissTarget.ids.length} sessions</span> from your pending queue. The note below applies to every one of them.
                </>
              ) : (
                <>
                  This will drop <span className="text-white">{dismissTarget.titles?.[0] || "the session"}</span> from your pending queue. Add an optional note so the audit trail knows why.
                </>
              )}
            </p>
            {dismissTarget.ids.length > 1 && (
              <ul className="text-[10px] text-[#525252] leading-relaxed mb-3 max-h-24 overflow-y-auto list-disc pl-4">
                {dismissTarget.titles.slice(0, 8).map((t, i) => <li key={i}>{t || "Untitled"}</li>)}
                {dismissTarget.titles.length > 8 && <li>… and {dismissTarget.titles.length - 8} more</li>}
              </ul>
            )}
            <textarea
              rows={3}
              value={dismissNote}
              onChange={(e) => setDismissNote(e.target.value.slice(0, 300))}
              placeholder="e.g. Player didn't attend, or session was a warm-up drill only."
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00] resize-y"
              data-testid="dismiss-feedback-note"
            />
            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-[#262626]">
              <button
                type="button"
                onClick={() => setDismissTarget(null)}
                disabled={dismissing}
                className="px-4 py-2 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white"
                data-testid="dismiss-feedback-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={runDismiss}
                disabled={dismissing}
                data-testid="dismiss-feedback-confirm"
                className="px-4 py-2 text-xs uppercase tracking-wider bg-[#525252] text-white hover:bg-[#404040] inline-flex items-center gap-1 disabled:opacity-50"
              >
                <X className="w-3 h-3" />
                {dismissing ? "Dismissing…" : (dismissTarget.ids.length > 1 ? `Dismiss ${dismissTarget.ids.length}` : "Dismiss session")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Iter171 — Team feedback: one shared recap sent to every player in
          the group. Reuses PostSessionFeedbackModal via a `teamStudentIds`
          prop so the same UI drives both flows. */}
      {team && (
        <PostSessionFeedbackModal
          open={!!team}
          onClose={() => setTeam(null)}
          onSaved={() => { setTeam(null); load(); }}
          bookingId={team.items[0].booking_id}
          studentId={team.items[0].student_id}
          studentName={`${team.items.length} players (team recap)`}
          sessionTitle={team.items[0].title}
          sport={team.items[0].sport || sport}
          teamStudentIds={team.items.map((it) => it.student_id)}
          teamStudentNames={team.items.map((it) => it.student_name)}
        />
      )}
    </div>
  );
}
